import { TFunction } from "i18next"
import {
  IDashboardBehaviorRow,
  IDashboardBehaviorSelection,
} from "ts/interfaces/AdminDashboard"
import { DashboardBehaviorType } from "ts/types/AdminDashboard"
import dayjs from "utils/dayjs"

// The count's colour marks the category, not good or bad; a bonus uses
// success40 because success50 is under 4.5:1 on white.
export const BEHAVIOR_CATEGORY_COLOR: Record<DashboardBehaviorType, string> = {
  bonus: "mockup.success40",
  fine: "mockup.error50",
}

// What differs between the two categories, looked up by type.
export const BEHAVIOR_TYPE = {
  bonus: {
    countField: "bonus_count",
    labelKey: "adminDashboard.behaviourTypeBonus",
    trendKey: "adminDashboard.bonusTrend",
    shareKey: "adminDashboard.ofAllBonuses",
    noneInPeriodKey: "adminDashboard.noBonusBehaviourInPeriod",
  },
  fine: {
    countField: "fine_count",
    labelKey: "adminDashboard.behaviourTypeFine",
    trendKey: "adminDashboard.fineTrend",
    shareKey: "adminDashboard.ofAllFines",
    noneInPeriodKey: "adminDashboard.noFineBehaviourInPeriod",
  },
} as const

// "Aug 17–23", "Aug 30–Sep 5", or "Sep 29" for a one-day bucket
export const formatBucket = (from: string, to: string) => {
  const start = dayjs(from)
  const end = dayjs(to)
  if (start.isSame(end, "day")) return start.format("MMM D")
  if (start.isSame(end, "month"))
    return `${start.format("MMM D")}–${end.format("D")}`
  return `${start.format("MMM D")}–${end.format("MMM D")}`
}

// Tooltip: "September 6–12, 2026", "August 30 – September 5, 2026", and both
// years when the bucket crosses one
export const formatBucketLong = (from: string, to: string) => {
  const start = dayjs(from)
  const end = dayjs(to)
  if (start.isSame(end, "day")) return start.format("MMMM D, YYYY")
  if (start.isSame(end, "month"))
    return `${start.format("MMMM D")}–${end.format("D, YYYY")}`
  if (start.isSame(end, "year"))
    return `${start.format("MMMM D")} – ${end.format("MMMM D, YYYY")}`
  return `${start.format("MMMM D, YYYY")} – ${end.format("MMMM D, YYYY")}`
}

export const formatCompactCount = (value: number) =>
  value >= 1000 ? `${(value / 1000).toFixed(1)}k` : `${value}`

export type BehaviorTrend =
  | { kind: "new" }
  | { kind: "same" }
  | { kind: "up" | "down"; percent: number }

// A change that rounds to 0% reads as unchanged, like the KPI trends.
export const getBehaviorTrend = (row: IDashboardBehaviorRow): BehaviorTrend => {
  if (row.prior_item_count === 0) return { kind: "new" }
  const delta = row.item_count - row.prior_item_count
  const percent = Math.round((Math.abs(delta) / row.prior_item_count) * 100)
  if (percent === 0) return { kind: "same" }
  return { kind: delta > 0 ? "up" : "down", percent }
}

// BED's total is nullable; without it, a full page means there may be more.
export const hasMoreThan = (
  allObjectsCount: number | null | undefined,
  loadedRows: number,
  shown: number,
) =>
  allObjectsCount === null
    ? loadedRows >= shown
    : (allObjectsCount ?? 0) > shown

// BED rejects a title over 255 code points, so a longer one is never sent.
const MAX_TITLE_LENGTH = 255

// A broken selection in the URL falls back to the totals without a message.
export const parseBehaviorSelection = (
  rawType: string | null,
  rawTitle: string | null,
): IDashboardBehaviorSelection | null => {
  if (rawType !== "bonus" && rawType !== "fine") return null
  if (!rawTitle?.trim() || [...rawTitle].length > MAX_TITLE_LENGTH) return null
  return { type: rawType, title: rawTitle }
}

const normalizeTitle = (title: string) => title.trim().toLowerCase()

// Same rule as BED's series filter, so a row is marked exactly when the chart
// shows its data.
export const isSameBehavior = (
  a: IDashboardBehaviorSelection | null,
  b: IDashboardBehaviorSelection | null,
) =>
  !!a &&
  !!b &&
  a.type === b.type &&
  normalizeTitle(a.title) === normalizeTitle(b.title)

// React escapes the text, so interpolations that carry a title skip
// i18next's own escaping ("Q&A", not "Q&amp;A").
export const RAW_INTERPOLATION = { interpolation: { escapeValue: false } }

// "Late to class (fine)"
export const behaviorLabel = (
  t: TFunction,
  { type, title }: IDashboardBehaviorSelection,
) =>
  t("adminDashboard.behaviourLabel", {
    ...RAW_INTERPOLATION,
    title,
    type: t(BEHAVIOR_TYPE[type].labelKey),
  })

const MAX_SLUG_LENGTH = 40

// Safe for a filename: the PNG export doesn't sanitise it. NFKD doesn't
// decompose "ł", so it's mapped by hand.
export const toBehaviorSlug = (title: string) =>
  title
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/gi, "l")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_SLUG_LENGTH)
    .replace(/-+$/, "") || "behaviour"
